// Added By Farhan Khan on 24-Dec-2025
// Enhanced By System on 29-Jan-2026 - Added summary cards and date range filter
import React, { useEffect, useState, useMemo } from "react";
import {
  Button,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Table,
  Card,
  Stack,
} from "react-bootstrap";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import solisPowerApi from "../../api/solisPowerApi";
import { useLocation } from "react-router-dom";
import Badge from "react-bootstrap/Badge";
import {
  formatDate,
  formatDateForView,
  getUserDetails,
} from "../../utils/helper";
import DateRangePicker from "rsuite/DateRangePicker";
import "rsuite/DateRangePicker/styles/index.css";

// Added by Jaseem 11-2-2026
import { DataGrid } from "@mui/x-data-grid";
import TextField from "@mui/material/TextField";
//=====================================================

import { Link } from "react-router-dom";
import InfoPill from "../InfoPill";
import Main from "../layout/Main";
import { ROUTES } from "../../constants/webRouting";
import PERMISSIONS from "../../constants/permissions";

const QuotationList = () => {
  const userInfo = getUserDetails();
  const location = useLocation();
  const navigate = useNavigate();
  const [body, setBody] = useState([]);
  const [quotations, setQuotations] = useState();

  // Date filter (same UI/logic as Lead List)
  const [dateRange, setDateRange] = React.useState([]);

  //Added By Jaseem 11-02-2026
  const [rows, setRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [leadsData ,setLeadOptions] = useState([]);
  const handleSearch = (value) => {
    setSearchText(value);
  };
  async function init() {
    const result = await solisPowerApi.fetchQuotations();
    console.log("result ==>", result);
    if (result) {
      setBody(result);
      setQuotations(result);

      const mappedRows = result.map((item, index) => ({
        id: item.id || index, // DataGrid ke liye mandatory
        estimate_number: item.estimate_number || "-",
        createddate: item.createddate || "-",
        customer_name: item.customer_name || "-",
        project_name: item.project_name || "-",
        quotation_date: item.quotation_date || "-",
        grand_total: item.grand_total || "-",
        status: item.status || "-",
      }));

      setRows(mappedRows);
    } else {
      setBody([]);
      setQuotations([]);
    }
    const leads = await solisPowerApi.fetchLeadsForQuotation();
    if (leads && Array.isArray(leads)) {
      setLeadOptions(leads.map((l) => ({
        value: l.id,
        label: `${l.lead_name || ""}${l.enquiry_no ? ` (${l.enquiry_no})` : ""}`,
      })));
    } else {
      setLeadOptions([]);
    }
  }
  useEffect(() => {
    init();
  }, []);

  // Consolidate all filtering (Date + Search) into useMemo
  const filteredRows = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    
    return rows.filter((item) => {
      // 1. Date Filter Logic (Normalized to start/end of day)
      let passDate = true;
      if (Array.isArray(dateRange) && dateRange.length === 2 && (dateRange[0] || dateRange[1])) {
        if (!item.quotation_date || item.quotation_date === "-") {
          passDate = false;
        } else {
          try {
            const qDate = new Date(item.quotation_date);
            qDate.setHours(0, 0, 0, 0);

            if (dateRange[0]) {
              const fromDate = new Date(dateRange[0]);
              fromDate.setHours(0, 0, 0, 0);
              if (qDate < fromDate) passDate = false;
            }

            if (dateRange[1]) {
              const toDate = new Date(dateRange[1]);
              toDate.setHours(23, 59, 59, 999);
              if (qDate > toDate) passDate = false;
            }
          } catch (err) {
            passDate = false;
          }
        }
      }

      // 2. Search Filter Logic
      let passSearch = true;
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        passSearch = Object.values(item).some((field) =>
          field && field.toString().toLowerCase().includes(searchLower)
        );
      }

      return passDate && passSearch;
    });
  }, [rows, dateRange, searchText]);

  // Sync summary counts with filtered data
  const summaryCounts = useMemo(() => {
    const dataToCount = filteredRows || [];
    return {
      total: dataToCount.length,
      sent: dataToCount.filter((q) => q.status === "Sent").length,
      draft: dataToCount.filter((q) => q.status === "Draft").length,
      approved: dataToCount.filter((q) => q.status === "Approved").length,
      rejected: dataToCount.filter((q) => q.status === "Rejected").length,
    };
  }, [filteredRows]);

  const columns = [
    {
      field: "estimate_number",
      headerName: "Estimate Number",
      flex: 1,
      renderCell: (params) => {
        const row = params.row;
        return (
          <Link
            to={`${ROUTES.QUOTATION.VIEW.replace(":id", row.id)}`}
            state={row}
            style={{
              color: "#1a73e8",
              textDecoration: "none",
              fontWeight: 600,
            }}
          >
            {row.estimate_number}
          </Link>
        );
      },
    },
    { field: "customer_name", headerName: "Customer", flex: 1 },
    { field: "project_name", headerName: "Project Name", flex: 1 },
    {
      field: "quotation_date",
      headerName: "Quotation Date",
      flex: 1,
      renderCell: (params) =>
        params.row?.quotation_date
          ? formatDateForView(params.row.quotation_date)
          : "",
    },
    {
      field: "grand_total",
      headerName: "Grand Total",
      flex: 1,
      renderCell: (params) => {
        const row = params.row;
        return (
          <span
            className="fw-bold"
            style={{
              color: "#364794",
              fontSize: "0.95rem",
            }}
          >
            ₹ {parseFloat(row.grand_total || 0).toFixed(2)}
          </span>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => {
        const row = params.row;
        return (
          <Badge
            bg={
              row.status === "Approved"
                ? "success"
                : row.status === "Draft"
                  ? "secondary"
                  : "info"
            }
          >
            {row.status}
          </Badge>
        );
      },
    },
  ];
  const createQuotation = () => {
    navigate(`/quotations/e`);
  };

  return (
    <Main>
      <Container fluid className="px-3 px-md-4 px-lg-5">
        {/* Added By System on 29-Jan-2026 - Summary Cards Dashboard */}
        <Row className="mb-4 mb-md-5">
          <Col lg={12}>
            <div className="row g-3 g-md-4">
              <div
                className="col-12 col-sm-6 col-md-4 col-lg"
                style={{ minWidth: "180px", flex: "1 1 0" }}
              >
                <Card
                  className="border-0 h-100 transition-card"
                  style={{
                    borderRadius: "16px",
                    backgroundColor: "#ffffff",
                    boxShadow:
                      "0 2px 8px rgba(54, 71, 148, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    border: "1px solid rgba(54, 71, 148, 0.06)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 16px rgba(54, 71, 148, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 2px 8px rgba(54, 71, 148, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)";
                  }}
                >
                  <Card.Body className="text-center px-3 py-3">
                    <i
                      className="fa fa-file-text mb-2"
                      style={{
                        color: "#364794",
                        fontSize: "1.5rem",
                        lineHeight: "1",
                      }}
                    ></i>
                    <div
                      className="text-muted mb-1"
                      style={{
                        fontSize: "0.7rem",
                        fontWeight: "600",
                        letterSpacing: "0.3px",
                        textTransform: "uppercase",
                      }}
                    >
                      Total Quotations
                    </div>
                    <div
                      className="mb-0 fw-bold"
                      style={{
                        color: "#364794",
                        fontSize: "1.5rem",
                        lineHeight: "1.2",
                        letterSpacing: "-0.3px",
                      }}
                    >
                      {summaryCounts.total}
                    </div>
                  </Card.Body>
                </Card>
              </div>
              <div
                className="col-12 col-sm-6 col-md-4 col-lg"
                style={{ minWidth: "180px", flex: "1 1 0" }}
              >
                <Card
                  className="border-0 h-100 transition-card"
                  style={{
                    borderRadius: "16px",
                    borderLeft: "4px solid #0dcaf0",
                    borderTop: "none",
                    borderRight: "none",
                    borderBottom: "none",
                    backgroundColor: "#ffffff",
                    boxShadow:
                      "0 2px 8px rgba(13, 202, 240, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    borderLeftWidth: "4px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 16px rgba(13, 202, 240, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 2px 8px rgba(13, 202, 240, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)";
                  }}
                >
                  <Card.Body className="text-center px-3 py-3">
                    <i
                      className="fa fa-paper-plane mb-2"
                      style={{
                        color: "#0dcaf0",
                        fontSize: "1.5rem",
                        lineHeight: "1",
                      }}
                    ></i>
                    <div
                      className="text-muted mb-1"
                      style={{
                        fontSize: "0.7rem",
                        fontWeight: "600",
                        letterSpacing: "0.3px",
                        textTransform: "uppercase",
                      }}
                    >
                      Sent
                    </div>
                    <div
                      className="mb-0 fw-bold"
                      style={{
                        color: "#0dcaf0",
                        fontSize: "1.5rem",
                        lineHeight: "1.2",
                        letterSpacing: "-0.3px",
                      }}
                    >
                      {summaryCounts.sent}
                    </div>
                  </Card.Body>
                </Card>
              </div>
              <div
                className="col-12 col-sm-6 col-md-4 col-lg"
                style={{ minWidth: "180px", flex: "1 1 0" }}
              >
                <Card
                  className="border-0 h-100 transition-card"
                  style={{
                    borderRadius: "16px",
                    borderLeft: "4px solid #6c757d",
                    borderTop: "none",
                    borderRight: "none",
                    borderBottom: "none",
                    backgroundColor: "#ffffff",
                    boxShadow:
                      "0 2px 8px rgba(108, 117, 125, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    borderLeftWidth: "4px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 16px rgba(108, 117, 125, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 2px 8px rgba(108, 117, 125, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)";
                  }}
                >
                  <Card.Body className="text-center px-3 py-3">
                    <i
                      className="fa fa-edit mb-2"
                      style={{
                        color: "#6c757d",
                        fontSize: "1.5rem",
                        lineHeight: "1",
                      }}
                    ></i>
                    <div
                      className="text-muted mb-1"
                      style={{
                        fontSize: "0.7rem",
                        fontWeight: "600",
                        letterSpacing: "0.3px",
                        textTransform: "uppercase",
                      }}
                    >
                      Draft
                    </div>
                    <div
                      className="mb-0 fw-bold"
                      style={{
                        color: "#6c757d",
                        fontSize: "1.5rem",
                        lineHeight: "1.2",
                        letterSpacing: "-0.3px",
                      }}
                    >
                      {summaryCounts.draft}
                    </div>
                  </Card.Body>
                </Card>
              </div>
              <div
                className="col-12 col-sm-6 col-md-4 col-lg"
                style={{ minWidth: "180px", flex: "1 1 0" }}
              >
                <Card
                  className="border-0 h-100 transition-card"
                  style={{
                    borderRadius: "16px",
                    borderLeft: "4px solid #198754",
                    borderTop: "none",
                    borderRight: "none",
                    borderBottom: "none",
                    backgroundColor: "#ffffff",
                    boxShadow:
                      "0 2px 8px rgba(25, 135, 84, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    borderLeftWidth: "4px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 16px rgba(25, 135, 84, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 2px 8px rgba(25, 135, 84, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)";
                  }}
                >
                  <Card.Body className="text-center px-3 py-3">
                    <i
                      className="fa fa-check-circle mb-2"
                      style={{
                        color: "#198754",
                        fontSize: "1.5rem",
                        lineHeight: "1",
                      }}
                    ></i>
                    <div
                      className="text-muted mb-1"
                      style={{
                        fontSize: "0.7rem",
                        fontWeight: "600",
                        letterSpacing: "0.3px",
                        textTransform: "uppercase",
                      }}
                    >
                      Approved
                    </div>
                    <div
                      className="mb-0 fw-bold"
                      style={{
                        color: "#198754",
                        fontSize: "1.5rem",
                        lineHeight: "1.2",
                        letterSpacing: "-0.3px",
                      }}
                    >
                      {summaryCounts.approved}
                    </div>
                  </Card.Body>
                </Card>
              </div>
              <div
                className="col-12 col-sm-6 col-md-4 col-lg"
                style={{ minWidth: "180px", flex: "1 1 0" }}
              >
                <Card
                  className="border-0 h-100 transition-card"
                  style={{
                    borderRadius: "16px",
                    borderLeft: "4px solid #dc3545",
                    borderTop: "none",
                    borderRight: "none",
                    borderBottom: "none",
                    backgroundColor: "#ffffff",
                    boxShadow:
                      "0 2px 8px rgba(220, 53, 69, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    borderLeftWidth: "4px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                    e.currentTarget.style.boxShadow =
                      "0 8px 16px rgba(220, 53, 69, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 2px 8px rgba(220, 53, 69, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)";
                  }}
                >
                  <Card.Body className="text-center px-3 py-3">
                    <i
                      className="fa fa-times-circle mb-2"
                      style={{
                        color: "#dc3545",
                        fontSize: "1.5rem",
                        lineHeight: "1",
                      }}
                    ></i>
                    <div
                      className="text-muted mb-1"
                      style={{
                        fontSize: "0.7rem",
                        fontWeight: "600",
                        letterSpacing: "0.3px",
                        textTransform: "uppercase",
                      }}
                    >
                      Rejected
                    </div>
                    <div
                      className="mb-0 fw-bold"
                      style={{
                        color: "#dc3545",
                        fontSize: "1.5rem",
                        lineHeight: "1.2",
                        letterSpacing: "-0.3px",
                      }}
                    >
                      {summaryCounts.rejected}
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </div>
          </Col>
        </Row>

        <Row className="">
          <Col lg={12} className="">
            <Row className="align-items-end my-2">
              {/* Added By Jaseem 11-02-2026 */}
              <Col lg={2} md={4} xl={2} sm={12} className="">
                <div className="leads-header">
                  <div className="leads-actions">
                    <TextField
                      size="small"
                      placeholder="Search Quotation..."
                      className="custom-search-field"
                      value={searchText}
                      onChange={(e) => handleSearch(e.target.value)}
                    />
                  </div>
                </div>
              </Col>
              {/* =================================================== */}
              {/* Added By System on 29-Jan-2026 - DateRangePicker (same as LeadList) */}
              <Col lg={2} md={4} sm={12} className="d-flex align-items-center">
                <DateRangePicker
                  value={dateRange}
                  onChange={(e) => {
                    setDateRange(e);
                  }}
                  character=" – "
                />
              </Col>

              <Col lg={2} md={4} sm={12} className="py-1">
                <div className="d-flex py-1 px-2 ">
                  <InfoPill
                    left="Total"
                    right={filteredRows?.length}
                    rightClasses="bg-custom text-white border-white fw-bold"
                    leftClasses="bg-white  fw-bold "
                  />
                </div>
              </Col>
              <Col
                sm={7}
                md={5}
                lg={5}
                className="d-flex flex-col justify-content-end align-items-end new-lead "
              >
                <Stack direction="horizontal" gap={2}>
                  {[PERMISSIONS.MODIFY_ALL].some((permission) => {
                    return userInfo?.permissions
                      .map((p) => p.name)
                      .includes(permission);
                  }) && (
                    <Button
                      size="sm"
                      className="btn-create"
                      onClick={() => createQuotation(true)}
                    >
                      New Quotation
                    </Button>
                  )}
                </Stack>
              </Col>
            </Row>
            {/* Added By Jaseem 11-02-2026 */}
            <Row>
              <div className="leads-page">
                {/* Header */}

                {/* DataGrid */}
                <div
                  style={{
                    height: 400,
                    width: "100%",
                    marginTop: "20px",
                    boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",
                  }}
                >
                  <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    pageSize={25}
                    rowsPerPageOptions={[25]}
                    getRowHeight={() => "auto"}
                    disableRowSelectionOnClick
                    className="custom-datagrid"
                  />
                </div>
              </div>
            </Row>
            {/* ==========================End By Jaseem ============================================= */}
          </Col>
        </Row>
      </Container>
    </Main>
  );
};
export default QuotationList;
